// DropzoneField.tsx (or define just above the Registration component)
import React from "react";
import { toast } from "react-toastify";

const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const MAX_BYTES = 5 * 1024 * 1024; // 5MB

type DropzoneFieldProps = {
  id: string;
  name: "uploadPanCard" | "uploadGstCertificate";
  label: string;
  file: File | null;
  accept?: string;
  error?: string;
  touched?: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  setFieldError: (field: string, message?: string) => void;
};

export const DropzoneField: React.FC<DropzoneFieldProps> = ({
  id,
  name,
  label,
  file,
  accept = ".pdf,.jpg,.jpeg,.png",
  error,
  touched,
  setFieldValue,
  setFieldTouched,
  setFieldError,
}) => {
  const [active, setActive] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const previewUrl = React.useMemo(() => {
    if (file && file.type.startsWith("image/")) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file]);

  React.useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const formatSize = (n: number) =>
    n >= 1024 * 1024
      ? `${(n / (1024 * 1024)).toFixed(2)} MB`
      : `${(n / 1024).toFixed(0)} KB`;

  const validateAndSet = (f: File | null) => {
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) {
      const msg = "Allowed types: PDF, JPG, PNG";
      setFieldError(name, msg);
      toast.error(msg);
      return;
    }
    if (f.size > MAX_BYTES) {
      const msg = "Max file size is 5MB";
      setFieldError(name, msg);
      toast.error(msg);
      return;
    }
    setFieldValue(name, f, true);
    setFieldTouched(name, true, false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setActive(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    validateAndSet(f);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.currentTarget.files?.[0] ?? null;
    validateAndSet(f);
    // reset input value so same file can be re-selected if needed
    e.currentTarget.value = "";
  };

  const openPicker = () => inputRef.current?.click();

  const hasError = !!(touched && error);

  return (
    <div className="mb-5">
      <label htmlFor={id} className="form-label">
        {label}
      </label>

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPicker()}
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={onDrop}
        className={`rounded p-3 d-flex gap-3 align-items-center border ${
          active ? "border-primary bg-light" : "border-secondary-subtle"
        } ${hasError ? "is-invalid input-error border-danger" : ""}`}
        style={{ borderStyle: "dashed", cursor: "pointer", minHeight: 110 }}
      >
        {/* Thumbnail (images only) */}
        <div
          className="rounded overflow-hidden d-flex align-items-center justify-content-center"
          style={{
            width: 80,
            height: 80,
            background: "#f8f9fa",
            flex: "0 0 auto",
          }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="preview"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <div className="text-muted small text-center px-2">No preview</div>
          )}
        </div>

        {/* Details + actions */}
        <div className="flex-grow-1">
          <div className="fw-semibold">
            {file ? file.name : "Drag & drop file here, or click to browse"}
          </div>
          <small className="text-muted">
            Accepted: PDF, JPG, PNG • Max 5MB
            {file ? ` • ${formatSize(file.size)}` : ""}
          </small>

          <div className="mt-2 d-flex gap-2">
            <button
              type="button"
              className="btn btn-lg btn-outline-primary"
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
            >
              Browse…
            </button>
            {file && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  setFieldValue(name, null, true);
                  setFieldTouched(name, true, false);
                }}
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="file"
          accept={accept}
          className="d-none"
          onChange={onChange}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {hasError && <div className="errorText">{String(error)}</div>}
    </div>
  );
};
